import { TASK_STATUS } from "../../constant/constant";
import { getCurrentUser } from "../../utils/currentUser";

// Only these three are per-assignee. Hold(7)/Duplicate(8)/Acknowledge(5)/
// Rework(6) stay task-level and are never offered in the per-person control.
export const WORK_STATUSES = [TASK_STATUS.TODO, TASK_STATUS.INPROGRESS, TASK_STATUS.COMPLETED];

export const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "?";

// Deterministic hue so one person is the same colour in every view.
const AVATAR_HUES = ["#0D9488", "#6366F1", "#0EA5E9", "#8B5CF6", "#F59E0B", "#EC4899", "#14B8A6", "#3B82F6"];
export const hueFor = (id = "") => {
  let h = 0;
  for (let i = 0; i < String(id).length; i++) h = (h * 31 + String(id).charCodeAt(i)) >>> 0;
  return AVATAR_HUES[h % AVATAR_HUES.length];
};

// getUserPicker returns `file` as a bare URL string; task docs carry
// `assigneeInfo.file.location`. Accept both.
const fileUrl = (f) => (typeof f === "string" ? f : f?.location || null);

/**
 * Normalises a task into a stable assignee array.
 *
 * Falls back to the legacy scalar mirror so a response from an endpoint that
 * has not been migrated (or a cached one) still renders exactly one avatar
 * instead of an empty stack.
 */
export function normalizeAssignees(task) {
  if (Array.isArray(task?.assignees) && task.assignees.length) {
    return task.assignees.map((a) => ({
      // `_id` is the LINE id; `user` is the PERSON. Key rows on the line, but
      // identify the person (permissions, status writes) by `user`.
      lineId: String(a._id ?? ""),
      _id: String(a.user ?? a._id ?? ""),
      name: (a.name || a.userName || "").trim() || "Unknown",
      avatar: fileUrl(a.file) || fileUrl(a.info?.file),
      roleName: a.roleName || null,
      status: Number(a.status) || TASK_STATUS.TODO,
      completedAt: a.completedAt || null,
      statusUpdatedAt: a.statusUpdatedAt || null,
    }));
  }
  const legacyId = task?.assignee?._id || task?.assignee;
  const legacyName = (task?.assigneeName || task?.assigneeUserName || "").trim();
  if (!legacyId && !legacyName) return [];
  return [
    {
      lineId: String(legacyId || ""),
      _id: String(legacyId || ""),
      name: legacyName || "Unknown",
      avatar: fileUrl(task?.assigneeInfo?.file) || task?.assigneeFile || null,
      roleName: task?.assigneeRoleName || null,
      status: Number(task?.status) || TASK_STATUS.TODO,
      completedAt: null,
      statusUpdatedAt: null,
    },
  ];
}

export function completionSummary(list) {
  const total = list.length;
  const done = list.filter((a) => a.status === TASK_STATUS.COMPLETED).length;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
}

// dayjs's relativeTime plugin is not registered anywhere in this repo, so this
// stays local rather than pulling in a new global side effect.
export function timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(then).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// ─── Assignee privacy ─────────────────────────────────────────────────────────
// Accepts both the populated-object and raw-id shapes, like AssigneeGroup does.
const idOf = (v) => String(v?._id ?? v ?? "");

export const isPrivateTask = (task) =>
  task?.privateAssignees === true || task?.privateAssignees === "true";

/**
 * PRESENTATION HINT ONLY. The server already redacts `assignees[]`,
 * `assigneeCount`, `status`, comments, activity and the work log before they
 * reach the client — this exists so the UI does not render misleading artefacts
 * (a "1 of 1 done" progress bar, a "+N" that can never appear, a filter chip
 * that returns nothing) on already-redacted data. It is NOT the security
 * boundary and must never be the only thing between a user and other names.
 *
 * Privileged: creator (assignedBy), admin, support, acknowledgedBy.
 * Defaults from getCurrentUser(); callers that derive identity differently
 * (TaskDetails reads the JWT) pass overrides — same contract as AssigneeGroup.
 */
export function canSeeAssignees(task, { currentUserId, isAdmin } = {}) {
  if (!isPrivateTask(task)) return true;
  const me = getCurrentUser();
  if (isAdmin ?? me.isAdmin) return true;
  const uid = String(currentUserId ?? me.userId ?? "");
  if (!uid) return false;
  return (
    uid === idOf(task?.assignedBy) ||
    uid === idOf(task?.support) ||
    uid === idOf(task?.acknowledgedBy)
  );
}

/** True when this viewer must be shown the solo illusion. */
export const isSoloView = (task, opts) =>
  isPrivateTask(task) && !canSeeAssignees(task, opts);

/**
 * The roster this viewer may render. A no-op on correctly-redacted data; it is
 * a cheap guard against a stale cached response leaking a "+N" chip. Fails
 * CLOSED — if the viewer is not in the list, render nothing rather than someone
 * else.
 */
export function visibleAssignees(task, opts = {}) {
  const list = normalizeAssignees(task);
  if (!isSoloView(task, opts)) return list;
  const uid = String(opts.currentUserId ?? getCurrentUser().userId ?? "");
  return list.filter((a) => String(a._id) === uid);
}
